import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export const Services = () => {
  const { language, t } = useLanguage();
  const seo = siteConfig.seo.services;

  useEffect(() => {
    document.title = t(seo.title);
  }, [language, seo, t]);

  return (
    <>
      <div className="flex flex-col">
        {/* Hero */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-accent/5">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="services-title">
              {language === 'fr' ? 'Nos Services' : 'Our Services'}
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {language === 'fr'
                ? 'Solutions complètes de conception, fabrication et intégration pour vos projets d’automatisation industrielle.'
                : 'Complete design, manufacturing, and integration solutions for your industrial automation projects.'}
            </p>
          </div>
        </section>

        {/* Services Details */}
        <section className="py-20" data-testid="services-details-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="divide-y divide-border">
              {siteConfig.services.map((service, idx) => (
                <motion.div
                  key={service.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start py-10 md:py-12 lg:py-14 first:pt-0 ${
                    idx % 2 === 1 ? '' : ''
                  }`}
                  data-testid={`service-${service.key}`}
                >
                  <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                      {t(service.title)}
                    </h2>
                    <ul className="space-y-3 mb-8">
                      {(language === 'fr' ? service.bulletsFR : service.bulletsEN).map((bullet, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <Button asChild className="bg-accent hover:bg-accent/90">
                      <Link to="/contact">
                        {language === 'fr' ? 'Demander une soumission' : 'Request a Quote'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                  <div className={`relative h-80 rounded-lg overflow-hidden shadow-xl mt-6 mb-0 lg:mt-0 ${
                    idx % 2 === 1 ? 'lg:order-1' : ''
                  }`}>
                    <img
                      src={`https://images.unsplash.com/photo-${idx === 0 ? '1581091226825-a6a2a5aee158' : idx === 1 ? '1581092160562-40aa08e78837' : '1518770660439-4636190af475'}?w=800&q=80`}
                      alt={t(service.title)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 md:py-32 bg-muted/30" data-testid="process-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Notre Processus' : 'Our Process'}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {language === 'fr'
                  ? 'Une approche éprouvée de la conception à la mise en service.'
                  : 'A proven approach from design to commissioning.'}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {siteConfig.process.steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.1 }}
                >
                  <Card>
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="font-heading text-xl font-bold text-primary">{idx + 1}</span>
                      </div>
                      <p className="text-sm font-medium">{t(step)}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="services-cta-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              {language === 'fr' ? 'Discutons de votre projet' : "Let's Discuss Your Project"}
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Contactez-nous pour obtenir une soumission personnalisée.'
                : 'Contact us for a personalized quote.'}
            </p>
            <Button asChild size="lg" variant="secondary" className="font-bold uppercase tracking-wide">
              <Link to="/contact">{t(siteConfig.brand.primaryCTA)}</Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};